import React, { useMemo } from 'react';
import { AuthorizedMedia } from '../AuthorizedMedia';
import { ComplaintStarRating } from '../ComplaintStarRating';
import { complaintRatingFromRaw } from '../../utils/complaintRating';
import {
  complaintCommentsFromRaw,
  latestRcaPaCaFromRaw,
} from '../../utils/complaintResolution';
import {
  formatComplaintStatusLabel,
  getComplaintStatusBadgeClass,
} from '../../utils/complaintStatus';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { formatComplaintQuantity, formatComplaintUom } from '../../utils/validation';
import {
  complaintTatDisplayValue,
  createdByLabel,
  formatReportedDateTime,
} from './backendAssignedComplaintRows';
import { TrackingStatusButton } from './TrackingStatusButton';

const getStatusClasses = (status) => getComplaintStatusBadgeClass(status);

const isVideoUrl = (url, contentType) => {
  const ct = String(contentType || '').toLowerCase();
  if (ct.startsWith('video/')) return true;
  return /\.(mp4|webm|ogg|mov|m4v|mkv)(\?|$)/i.test(String(url || ''));
};

const mapRawAttachmentToExisting = (a) => {
  const id = a?.id ?? a?.attachment_id;
  const url = resolveAssetUrl(
    a?.url || a?.file_url || a?.signed_url || a?.blob_url || a?.image_url || ''
  );
  if (!url) return null;
  return {
    id: id != null && String(id) !== '' ? String(id) : null,
    url,
    contentType: a?.content_type || a?.contentType || '',
    filename: String(a?.filename || a?.file_name || a?.name || '').trim(),
  };
};

const attachmentListFromRaw = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (Array.isArray(raw.attachments) && raw.attachments.length > 0) return raw.attachments;
  if (Array.isArray(raw.attachments_url) && raw.attachments_url.length > 0)
    return raw.attachments_url;
  if (Array.isArray(raw.attachment_urls) && raw.attachment_urls.length > 0)
    return raw.attachment_urls;
  if (Array.isArray(raw.attachments)) return raw.attachments;
  if (Array.isArray(raw.attachments_url)) return raw.attachments_url;
  return null;
};

const parseExistingAttachmentsFromRaw = (raw) => {
  const out = [];
  if (!raw || typeof raw !== 'object') return out;
  const list = attachmentListFromRaw(raw);
  if (list && list.length > 0) {
    list.forEach((a) => {
      const row = mapRawAttachmentToExisting(a);
      if (row) out.push(row);
    });
    return out;
  }
  const legacy = resolveAssetUrl(raw.image_url || raw.imageUrl || '');
  if (legacy) {
    const legacyId = raw?.image_attachment_id ?? raw?.attachment_id;
    out.push({
      id: legacyId != null && String(legacyId) !== '' ? String(legacyId) : null,
      url: legacy,
      contentType: '',
      filename: '',
    });
  }
  return out;
};

const ComplaintDetailAttachmentGallery = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-gray-500">No attachments uploaded.</p>;
  }
  return (
    <div className="flex flex-wrap gap-3">
      {items.map((a, idx) => {
        const key = `${a.id ?? 'x'}-${idx}-${a.url}`;
        const vid = isVideoUrl(a.url, a.contentType);
        return (
          <div key={key} className="flex w-32 max-w-[40%] flex-shrink-0 flex-col gap-1 sm:max-w-none">
            <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-gray-200 bg-black/5">
              {vid ? (
                <AuthorizedMedia
                  as="video"
                  src={a.url}
                  className="h-full w-full object-cover"
                  muted
                  playsInline
                  videoControls
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <AuthorizedMedia
                  src={a.url}
                  alt={a.filename || `Attachment ${idx + 1}`}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
            </div>
            {a.filename ? (
              <p
                className="line-clamp-2 break-all text-[11px] leading-snug text-gray-600"
                title={a.filename}
              >
                {a.filename}
              </p>
            ) : (
              <p className="text-[11px] text-gray-400">{vid ? 'Video' : 'Image'}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Read-only complaint detail (fields, attachments, product info, comments, RCA/PA/CA).
 * Matches the main Complaints page detail drawer, without reply/review forms.
 */
export function ComplaintDetailReadView({
  raw,
  className = '',
  assignedToLabel,
  onTrackStatusClick,
}) {
  const isItsm = String(raw?.complaint_type || '').toUpperCase() === 'ITSM';
  const statusLabel = formatComplaintStatusLabel(raw?.status);
  const rating = useMemo(() => complaintRatingFromRaw(raw), [raw]);
  const viewAttachments = useMemo(() => parseExistingAttachmentsFromRaw(raw || {}), [raw]);
  const comments = useMemo(() => complaintCommentsFromRaw(raw), [raw]);
  const { rca, pa, ca } = useMemo(() => latestRcaPaCaFromRaw(raw), [raw]);

  const reportedDate = formatReportedDateTime(raw?.created_at);
  const issueText = isItsm
    ? (raw?.issue_description || '').trim() || '—'
    : (raw?.remarks || raw?.issue_description || '').trim() || '—';

  const paClassName =
    pa && /review/i.test(String(pa)) ? 'font-medium text-amber-700' : 'font-medium text-gray-900';

  const assignedToDisplay = useMemo(() => {
    if (assignedToLabel !== undefined) {
      const t = String(assignedToLabel ?? '').trim();
      return t || '—';
    }
    const fromApi = raw?.assigned_to_name || raw?.assigned_to;
    return fromApi != null && String(fromApi).trim() !== '' ? String(fromApi).trim() : '—';
  }, [assignedToLabel, raw]);

  if (!raw) return null;

  return (
    <div className={`space-y-4 text-sm ${className}`}>
      {rating?.rating >= 1 ? (
        <div className="rounded-lg border border-amber-200/80 bg-amber-50/50 px-4 py-3">
          <p className="mb-2 font-semibold text-gray-900">Rating</p>
          <ComplaintStarRating value={rating.rating} readOnly size="md" />
          {rating.feedback ? (
            <p className="mt-2 whitespace-pre-wrap text-gray-700">{rating.feedback}</p>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <div>
          <p className="text-gray-500">Complaint ID</p>
          <p className="font-medium text-gray-900">
            {raw.acknowledgement_number != null && String(raw.acknowledgement_number).trim() !== ''
              ? String(raw.acknowledgement_number)
              : '—'}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Location</p>
          <p className="font-medium text-gray-900">{raw.location_name || '—'}</p>
        </div>
        <div>
          <p className="text-gray-500">Reported Date</p>
          <p className="font-medium text-gray-900">{reportedDate}</p>
        </div>
        <div>
          <p className="text-gray-500">Type</p>
          <p className="font-medium text-gray-900">{raw.complaint_type || '—'}</p>
        </div>
        {isItsm ? (
          <>
            <div>
              <p className="text-gray-500">Date of Incident</p>
              <p className="font-medium text-gray-900">
                {raw.date_of_incident
                  ? new Date(raw.date_of_incident).toLocaleDateString()
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Issue Type</p>
              <p className="font-medium text-gray-900">
                {raw.issue_type_name ||
                  raw.noc_issue_type_name ||
                  raw.complaint_sub_type ||
                  '—'}
              </p>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-gray-500">Issue Type</p>
              <p className="font-medium text-gray-900">
                {raw.nature_of_complaint_name || raw.complaint_sub_type || '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Quantity</p>
              <p className="font-medium text-gray-900">{formatComplaintQuantity(raw.quantity)}</p>
            </div>
            <div>
              <p className="text-gray-500">UOM</p>
              <p className="font-medium text-gray-900">{formatComplaintUom(raw.uom)}</p>
            </div>
            <div>
              <p className="text-gray-500">Vehicle storage temperature</p>
              <p className="font-medium text-gray-900">
                {raw.vehicle_storage_temperature ?? raw.vehicleStorageTemperature ?? '—'}
              </p>
            </div>
          </>
        )}
        <div className={onTrackStatusClick ? 'sm:col-span-2' : ''}>
          <p className="text-gray-500">Status</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(
                statusLabel
              )}`}
            >
              {statusLabel}
            </span>
            {onTrackStatusClick ? (
              <TrackingStatusButton onClick={onTrackStatusClick} />
            ) : null}
          </div>
        </div>
        <div>
          <p className="text-gray-500">TAT (Turnaround Time)</p>
          <p className="font-medium text-gray-900">{complaintTatDisplayValue(raw)}</p>
        </div>
        <div>
          <p className="text-gray-500">Raised By</p>
          <p className="font-medium text-gray-900">{createdByLabel(raw)}</p>
        </div>
        <div>
          <p className="text-gray-500">Reporting Supervisor</p>
          <p className="font-medium text-gray-900">
            {raw.reporting_supervisor_name != null && String(raw.reporting_supervisor_name).trim()
              ? String(raw.reporting_supervisor_name).trim()
              : '—'}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Accountable Department</p>
          <p className="font-medium text-gray-900">{raw.department_name || '—'}</p>
        </div>
        <div>
          <p className="text-gray-500">Assigned To</p>
          <p className="font-medium text-gray-900">{assignedToDisplay}</p>
        </div>
        {!isItsm ? (
          <div>
            <p className="text-gray-500">Issue Type</p>
            <p className="font-medium text-gray-900">{raw.issue_type_name || '—'}</p>
          </div>
        ) : null}
      </div>

      <div>
        <p className="mb-1 font-semibold text-gray-900">
          {isItsm ? 'Issue Description' : 'Remarks'}
        </p>
        <p className="text-gray-600">{issueText}</p>
      </div>

      <div className="border-t border-gray-200 pt-3">
        <p className="mb-2 font-semibold text-gray-900">
          Attachments
          {viewAttachments.length > 0 ? ` (${viewAttachments.length})` : ''}
        </p>
        <ComplaintDetailAttachmentGallery items={viewAttachments} />
      </div>

      {!isItsm ? (
        <details className="border-t border-gray-200 pt-3" open>
          <summary className="cursor-pointer select-none font-semibold text-gray-900">
            Product Information
          </summary>
          <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <div>
              <p className="text-gray-500">Product Name</p>
              <p className="font-medium text-gray-900">
                {raw.complaint_product_name || raw.product_name || raw.productName || '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Product Category</p>
              <p className="font-medium text-gray-900">
                {raw.product_category_name || raw.productCategoryName || '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Product Sub Category</p>
              <p className="font-medium text-gray-900">
                {raw.product_subcategory_name || raw.productSubcategoryName || '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Batch No. (As Per Actual)</p>
              <p className="font-medium text-gray-900">{raw.batch_number || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">MFG Date</p>
              <p className="font-medium text-gray-900">{raw.mfg_date || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Use Before / Best Before</p>
              <p className="font-medium text-gray-900">{raw.use_best_before || '—'}</p>
            </div>
          </div>
        </details>
      ) : null}

      <details className="border-t border-gray-200 pt-3" open>
        <summary className="cursor-pointer select-none font-semibold text-gray-900">
          Complaint Information
        </summary>
        
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 py-4 sm:grid-cols-2">
            <div>
              <p className="text-gray-500">RCA</p>
              <p className="font-medium text-gray-900">{rca || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">PA (Preventive Action)</p>
              <p className={paClassName}>{pa || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">CA (Corrective Action)</p>
              <p className="font-medium text-gray-900">{ca || '—'}</p>
            </div>
          </div>
        <div className="mt-3 space-y-6">
          <div>
            <p className="mb-2 font-semibold text-gray-900">Comments</p>
            {comments.length === 0 ? (
              <p className="text-sm text-gray-500">No comments yet.</p>
            ) : (
              <ul className="max-h-64 space-y-3 overflow-y-auto pr-1">
                {comments.map((c) => {
                  const imgUrl = c?.image_url ? resolveAssetUrl(String(c.image_url)) : '';
                  const assignedNames = Array.isArray(c?.assigned_users)
                    ? c.assigned_users
                        .map((u) => u?.name)
                        .filter(Boolean)
                        .join(', ')
                    : '';
                  return (
                    <li
                      key={c.id ?? `${c.created_at}-${c.comment}`}
                      className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-gray-500">
                        <span className="font-medium text-gray-800">
                          {c.account_name || '—'}
                          {c.commented_by_type ? (
                            <span className="font-normal text-gray-500">
                              {' '}
                              · {c.commented_by_type}
                            </span>
                          ) : null}
                        </span>
                        <span>{formatReportedDateTime(c.created_at)}</span>
                      </div>
                      {c.status ? (
                        <p className="mt-1 text-xs text-gray-600">
                          Status:{' '}
                          <span className="font-medium">
                            {formatComplaintStatusLabel(c.status)}
                          </span>
                        </p>
                      ) : null}
                      {assignedNames ? (
                        <p className="mt-0.5 text-xs text-gray-600">Assigned: {assignedNames}</p>
                      ) : null}
                      <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">
                        {c.comment || '—'}
                      </p>
                      {imgUrl ? (
                        <div className="mt-2 h-24 w-24 overflow-hidden rounded-md border border-gray-200 bg-black/5">
                          <AuthorizedMedia
                            src={imgUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            onError={(ev) => {
                              ev.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </details>
    </div>
  );
}

export default ComplaintDetailReadView;
